import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Auth } from 'aws-amplify';
import { Box, Typography, Alert, Snackbar } from '@mui/material';
import Grid from '@mui/material/Grid';
import { ContentTitle, RightGrid } from '../ui/styled';
import BackIcon from '../common/Back/BackIcon';
import PageHeading from '../common/Heading/PageHeading';
import QueryBuilderToolbar from './QueryBuilderToolbar';
import QueryFieldsGrid from './QueryFieldsGrid';
import SqlPreviewPanel from './SqlPreviewPanel';
import QueryResultsGrid from './QueryResultsGrid';
import {
  addField,
  clearFields,
  setExecuting,
  setQueryResults,
  setError,
  setSuccessMessage,
  clearError,
  clearSuccessMessage,
  loadSavedQueries,
  loadQuery,
  updateSavedQueries,
  setCurrentQueryId
} from '../../redux/queryBuilder/queryBuilderSlice';
import { executeAdHocQuery } from './services/queryBuilderApi';
import { exportToCSV, validateExportData } from './utils/csvExporter';
import {
  getSavedQueries,
  saveQuery,
  deleteQuery as deleteStoredQuery,
  renameQuery as renameStoredQuery,
  duplicateQuery as duplicateStoredQuery,
  toggleFavorite as toggleStoredFavorite
} from './utils/queryStorage';
import SaveQueryDialog from './SaveQueryDialog';
import RenameQueryDialog from './RenameQueryDialog';
import QueryManagementDialog from './QueryManagementDialog';

const QueryBuilderPage = () => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [queryToRename, setQueryToRename] = useState(null);

  const {
    selectedFields,
    generatedSql,
    isExecuting,
    queryResults,
    error,
    successMessage,
    savedQueries,
    currentQueryId,
    hasUnsavedChanges
  } = useSelector((state) => state.queryBuilder);

  useEffect(() => {
    getUser().then((userData) => setUser(userData));
    // Load saved queries from localStorage
    const queries = getSavedQueries();
    dispatch(loadSavedQueries(queries));
  }, [dispatch]);

  const getUser = () => {
    return Auth.currentAuthenticatedUser()
      .then((userData) => userData)
      .catch(() => console.log('Not signed in'));
  };

  const handleAddField = () => {
    dispatch(addField());
  };

  const handleClearFields = () => {
    dispatch(clearFields());
  };

  const handleExecuteQuery = async () => {
    if (!generatedSql || !user?.signInUserSession?.accessToken?.jwtToken) {
      dispatch(setError('No query to execute or user not authenticated'));
      return;
    }

    dispatch(setExecuting(true));
    dispatch(clearError());

    try {
      const authorization = user.signInUserSession.accessToken.jwtToken;
      const response = await executeAdHocQuery({
        sql: generatedSql,
        authorization,
      });

      if (response.succeeded) {
        dispatch(setQueryResults(response));
        dispatch(setSuccessMessage(response.message || 'Query executed successfully'));
      } else {
        dispatch(setError(response.error || 'Query execution failed'));
      }
    } catch (err) {
      dispatch(setError(err.toString() || 'An error occurred while executing the query'));
    } finally {
      dispatch(setExecuting(false));
    }
  };

  const handleCloseError = () => {
    dispatch(clearError());
  };

  const handleCloseSuccess = () => {
    dispatch(clearSuccessMessage());
  };

  const handleExportCSV = () => {
    // Validate data
    const validation = validateExportData(queryResults?.data);
    if (!validation.valid) {
      dispatch(setError(validation.error));
      return;
    }

    try {
      // Export to CSV
      const filename = exportToCSV(queryResults.data);
      dispatch(setSuccessMessage(`Results exported to ${filename}`));
    } catch (err) {
      dispatch(setError(err.message || 'Failed to export results'));
    }
  };

  const handleSaveQuery = () => {
    setSaveDialogOpen(true);
  };

  const handleSaveQuerySubmit = (queryData) => {
    const result = saveQuery(queryData);
    if (result.success) {
      const queries = getSavedQueries();
      dispatch(updateSavedQueries(queries));
      dispatch(setCurrentQueryId(result.query.id));
      dispatch(setSuccessMessage(`Query "${result.query.name}" saved successfully`));
    } else {
      dispatch(setError(result.error || 'Failed to save query'));
    }
  };

  const handleLoadQuery = (query) => {
    if (hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Load this query anyway?')) {
        return;
      }
    }
    dispatch(loadQuery(query));
    dispatch(setSuccessMessage(`Query "${query.name}" loaded`));
  };

  const handleDeleteQuery = (queryId) => {
    if (!window.confirm('Are you sure you want to delete this query?')) {
      return;
    }
    const result = deleteStoredQuery(queryId);
    if (result.success) {
      const queries = getSavedQueries();
      dispatch(updateSavedQueries(queries));
      dispatch(setSuccessMessage('Query deleted successfully'));
    } else {
      dispatch(setError(result.error || 'Failed to delete query'));
    }
  };

  const handleRenameQuery = (query) => {
    setQueryToRename(query);
    setRenameDialogOpen(true);
  };

  const handleRenameQuerySubmit = (queryId, newName) => {
    const result = renameStoredQuery(queryId, newName);
    if (result.success) {
      const queries = getSavedQueries();
      dispatch(updateSavedQueries(queries));
      dispatch(setSuccessMessage(`Query renamed to "${newName}"`));
    } else {
      dispatch(setError(result.error || 'Failed to rename query'));
    }
  };

  const handleDuplicateQuery = (queryId) => {
    const result = duplicateStoredQuery(queryId);
    if (result.success) {
      const queries = getSavedQueries();
      dispatch(updateSavedQueries(queries));
      dispatch(setSuccessMessage(`Query "${result.query.name}" created`));
    } else {
      dispatch(setError(result.error || 'Failed to duplicate query'));
    }
  };

  const handleToggleFavorite = (queryId) => {
    const result = toggleStoredFavorite(queryId);
    if (result.success) {
      const queries = getSavedQueries();
      dispatch(updateSavedQueries(queries));
      const message = result.query.isFavorite ? 'Added to favorites' : 'Removed from favorites';
      dispatch(setSuccessMessage(message));
    } else {
      dispatch(setError(result.error || 'Failed to update favorite status'));
    }
  };

  const handleManageAll = () => {
    setManageDialogOpen(true);
  };

  const getCurrentQuery = () => {
    if (!currentQueryId) return null;
    return savedQueries.find(q => q.id === currentQueryId);
  };

  return (
    <>
      <ContentTitle container>
        <RightGrid item xs={12}>
          <BackIcon />
          <PageHeading title='Ad Hoc Query Builder' />
        </RightGrid>
      </ContentTitle>

      <Box sx={{ p: 2 }}>
        <QueryBuilderToolbar
          onAddField={handleAddField}
          onClearFields={handleClearFields}
          onExecuteQuery={handleExecuteQuery}
          onExportCSV={handleExportCSV}
          onSaveQuery={handleSaveQuery}
          isExecuting={isExecuting}
          hasFields={selectedFields.length > 0}
          hasSql={!!generatedSql}
          hasResults={!!queryResults?.data && queryResults.data.length > 0}
          savedQueries={savedQueries}
          currentQuery={getCurrentQuery()}
          onLoadQuery={handleLoadQuery}
          onDeleteQuery={handleDeleteQuery}
          onRenameQuery={handleRenameQuery}
          onDuplicateQuery={handleDuplicateQuery}
          onToggleFavorite={handleToggleFavorite}
          onManageAll={handleManageAll}
        />

        <Box sx={{ mb: 3 }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Selected Fields & Criteria
          </Typography>
          {selectedFields.length === 0 ? (
            <Alert severity='info'>
              Click "Add Field" to start building your query. Select a table, field, operator, and criteria for each row.
            </Alert>
          ) : (
            <QueryFieldsGrid selectedFields={selectedFields} />
          )}
        </Box>

        <SqlPreviewPanel sql={generatedSql} />

        {queryResults && <QueryResultsGrid results={queryResults} selectedFields={selectedFields} onExportCSV={handleExportCSV} />}
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity='error' sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity='success' sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <SaveQueryDialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        onSave={handleSaveQuerySubmit}
        currentQuery={getCurrentQuery()}
        selectedFields={selectedFields}
      />

      <RenameQueryDialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        onRename={handleRenameQuerySubmit}
        query={queryToRename}
      />

      <QueryManagementDialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        savedQueries={savedQueries}
        onLoadQuery={handleLoadQuery}
        onRenameQuery={handleRenameQuery}
        onDuplicateQuery={handleDuplicateQuery}
        onDeleteQuery={handleDeleteQuery}
        onToggleFavorite={handleToggleFavorite}
      />
    </>
  );
};

export default QueryBuilderPage;
